import { useEffect, useRef, useState, useCallback } from 'react'

// Proxy URL — server adds Authorization header (browser WS can't set headers)
// Always api.romea.ai regardless of CLAW_GATEWAY_URL
const DEEPGRAM_PROXY_URL = 'wss://api.romea.ai'

export interface DeepgramTranscript {
  speaker: string
  speakerId?: string
  text: string
  startMs: number
  endMs: number
  isFinal: boolean
  language?: string
}

export function useDeepgram(enabled: boolean, onTranscript: (t: DeepgramTranscript) => void) {
  const wsRef = useRef<WebSocket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const connect = useCallback(() => {
    if (!enabled) {
      setLastError('Not enabled')
      return
    }
    if (wsRef.current?.readyState === WebSocket.OPEN) return

    try {
      // Route through server-side proxy (adds Authorization header server-side)
      // keywords: bias toward 'Romea' — Deepgram often transcribes it as 'Romania', 'Romeo', 'Romia'
      const url = `${DEEPGRAM_PROXY_URL}/deepgram/listen?model=nova-3&encoding=linear16&sample_rate=16000&punctuate=true&diarize=true&interim_results=true&endpointing=500&language=en&keywords=Romea:5&keywords=romea:5`

      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        setIsConnected(true)
        setLastError(null)
        console.log('[Deepgram] connected')
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          const alt = data.channel?.alternatives?.[0]
          if (!alt) return

          const words = alt.words || []
          const speaker = words[0]?.speaker !== undefined ? `Speaker ${words[0].speaker + 1}` : 'Unknown'

          const transcript: DeepgramTranscript = {
            speaker,
            speakerId: words[0]?.speaker?.toString(),
            text: alt.transcript || '',
            startMs: words[0]?.start ? Math.round(words[0].start * 1000) : Date.now(),
            endMs: words[words.length - 1]?.end ? Math.round(words[words.length - 1].end * 1000) : Date.now(),
            isFinal: data.is_final ?? false,
            language: data.channel?.detected_language,
          }

          if (transcript.text) {
            onTranscript(transcript)
          }
        } catch (e) {
          console.warn('[Deepgram] parse error:', e)
        }
      }

      ws.onerror = (err) => {
        console.error('[Deepgram] error:', err)
        setLastError('WebSocket error')
        setIsConnected(false)
      }

      ws.onclose = (ev) => {
        setIsConnected(false)
        console.log('[Deepgram] closed:', ev.code, ev.reason)
        if (ev.code === 1006 && ev.reason) {
          setLastError(ev.reason)
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          connect()
        }, 5000)
      }
    } catch (err) {
      console.error('[Deepgram] connect failed:', err)
      setLastError(String(err))
    }
  }, [enabled, onTranscript])

  useEffect(() => {
    if (enabled) {
      connect()
    }
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [enabled, connect])

  const sendAudio = useCallback((pcmData: Int16Array) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(pcmData.buffer)
    }
  }, [])

  return { isConnected, lastError, sendAudio }
}
